import React, { useState } from 'react';
import { CreditCardIcon, SearchIcon, FilterIcon, ChevronRightIcon, CheckCircleIcon, ClockIcon, XCircleIcon, FileTextIcon, DownloadIcon } from 'lucide-react';
export function ExpenseClaimsList({
  locale = 'en-US',
  onViewExpense
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Sample expense data
  const expenses = [{
    id: 'EXP-2023-001',
    title: 'Client Meeting - ABC Corp',
    date: '2023-11-10',
    submittedDate: '2023-11-11',
    amount: 245.75,
    status: 'Pending Approval',
    category: 'Travel',
    submittedBy: 'John Doe'
  }, {
    id: 'EXP-2023-002',
    title: 'Office Supplies',
    date: '2023-11-08',
    submittedDate: '2023-11-09',
    amount: 89.99,
    status: 'Approved',
    category: 'Office',
    submittedBy: 'Jane Smith'
  }, {
    id: 'EXP-2023-003',
    title: 'Conference Registration',
    date: '2023-11-05',
    submittedDate: '2023-11-06',
    amount: 599.0,
    status: 'Reimbursed',
    category: 'Training',
    submittedBy: 'Robert Brown'
  }, {
    id: 'EXP-2023-004',
    title: 'Team Lunch',
    date: '2023-11-02',
    submittedDate: '2023-11-03',
    amount: 187.5,
    status: 'Reimbursed',
    category: 'Meals',
    submittedBy: 'John Doe'
  }, {
    id: 'EXP-2023-005',
    title: 'Software License',
    date: '2023-10-28',
    submittedDate: '2023-10-29',
    amount: 299.99,
    status: 'Rejected',
    category: 'Software',
    submittedBy: 'Jane Smith',
    rejectionReason: 'Duplicate expense claim'
  }, {
    id: 'EXP-2023-006',
    title: 'Marketing Materials',
    date: '2023-10-25',
    submittedDate: '2023-10-26',
    amount: 450.0,
    status: 'Draft',
    category: 'Marketing',
    submittedBy: 'Robert Brown'
  }];
  // Get status badge styling
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Reimbursed':
        return 'bg-blue-100 text-blue-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Draft':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-7613" />;
      case 'Reimbursed':
        return <CreditCardIcon className="h-5 w-5 text-blue-500" data-id="element-7614" />;
      case 'Pending Approval':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7615" />;
      case 'Rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-7616" />;
      case 'Draft':
      default:
        return <FileTextIcon className="h-5 w-5 text-gray-500" data-id="element-7617" />;
    }
  };
  // Filter expenses based on search term and filters
  const filteredExpenses = expenses.filter(expense => {
    // Filter by search term
    const matchesSearch = searchTerm === '' || expense.title.toLowerCase().includes(searchTerm.toLowerCase()) || expense.id.toLowerCase().includes(searchTerm.toLowerCase()) || expense.category.toLowerCase().includes(searchTerm.toLowerCase());
    // Filter by status
    const matchesStatus = statusFilter === 'all' || expense.status === statusFilter;
    // Filter by date (simplified for demo)
    let matchesDate = true;
    if (dateFilter === 'last7days') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      matchesDate = new Date(expense.date) >= sevenDaysAgo;
    } else if (dateFilter === 'last30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      matchesDate = new Date(expense.date) >= thirtyDaysAgo;
    }
    return matchesSearch && matchesStatus && matchesDate;
  });
  return <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7618">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7619">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7620">
          Expense Claims
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-7621">
          View and manage all expense claims
        </p>
      </div>
      {/* Search and filters */}
      <div className="p-4 border-b border-gray-200 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-7622">
        <div className="w-full sm:w-96" data-id="element-7623">
          <div className="relative rounded-md shadow-sm" data-id="element-7624">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7625">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-7626" />
            </div>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search expenses..." data-id="element-7627" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-7628">
          <div className="flex items-center" data-id="element-7629">
            <label htmlFor="status-filter" className="sr-only" data-id="element-7630">
              Filter by status
            </label>
            <select id="status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7631">
              <option value="all" data-id="element-7632">All Statuses</option>
              <option value="Draft" data-id="element-7633">Draft</option>
              <option value="Pending Approval" data-id="element-7634">Pending Approval</option>
              <option value="Approved" data-id="element-7635">Approved</option>
              <option value="Rejected" data-id="element-7636">Rejected</option>
              <option value="Reimbursed" data-id="element-7637">Reimbursed</option>
            </select>
          </div>
          <div className="flex items-center" data-id="element-7638">
            <label htmlFor="date-filter" className="sr-only" data-id="element-7639">
              Filter by date
            </label>
            <select id="date-filter" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7640">
              <option value="all" data-id="element-7641">All Dates</option>
              <option value="last7days" data-id="element-7642">Last 7 Days</option>
              <option value="last30days" data-id="element-7643">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>
      {/* Expense list */}
      <ul className="divide-y divide-gray-200" data-id="element-7644">
        {filteredExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-7645">
            No expense claims match your search criteria.
          </li> : filteredExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => onViewExpense && onViewExpense(expense)} data-id="element-7646">
              <div className="px-4 py-4 sm:px-6" data-id="element-7647">
                <div className="flex items-center justify-between" data-id="element-7648">
                  <div className="flex items-center space-x-3" data-id="element-7649">
                    <div className="flex-shrink-0" data-id="element-7650">
                      {getStatusIcon(expense.status)}
                    </div>
                    <div data-id="element-7651">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-7652">
                        {expense.title}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-7653">
                        {expense.id} • {expense.category} • Submitted{' '}
                        {formatDate(expense.submittedDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-7654">
                    <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(expense.status)}`} data-id="element-7655">
                      {expense.status}
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-7656">
                      {formatCurrency(expense.amount)}
                    </p>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-7657" />
                  </div>
                </div>
                {expense.status === 'Rejected' && <div className="mt-2" data-id="element-7658">
                    <p className="text-sm text-red-600" data-id="element-7659">
                      Reason: {expense.rejectionReason}
                    </p>
                  </div>}
              </div>
            </li>)}
      </ul>
      {/* Pagination (simplified) */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-7660">
        <div className="flex-1 flex justify-between sm:hidden" data-id="element-7661">
          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-7662">
            Previous
          </button>
          <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-7663">
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-7664">
          <div data-id="element-7665">
            <p className="text-sm text-gray-700" data-id="element-7666">
              Showing <span className="font-medium" data-id="element-7667">1</span> to{' '}
              <span className="font-medium" data-id="element-7668">{filteredExpenses.length}</span> of{' '}
              <span className="font-medium" data-id="element-7669">{filteredExpenses.length}</span>{' '}
              results
            </p>
          </div>
          <div data-id="element-7670">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-7671">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-7672">
                <span className="sr-only" data-id="element-7673">Previous</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-7674">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-7675" />
                </svg>
              </button>
              <button aria-current="page" className="z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium" data-id="element-7676">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-7677">
                <span className="sr-only" data-id="element-7678">Next</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-7679">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-7680" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}